<template>
  <div class="otp-verification" data-testid="otp-verification">
    <img src="../assets/logo.png" class="mb-4" width="140">
    <v-alert
      class="otp-verification__error text-left"
      color="red"
      type="error"
      v-if="error"
      data-testid="otp-verification__error"
    >
      {{error}}
    </v-alert>
    <div v-else class="text-center mb-4" data-testid="otp-verification__message">
      <p>{{ $t('label.otp_sent') }}</p>
      <p class="text-caption">{{ userEmail }}</p>
    </div>
    <v-form v-model="valid">
      <v-text-field
        class="otp-verification__code"
        :placeholder="$t('field.otp_code')"
        v-model="model.otpCode"
        hide-details
        type="text"
        maxlength="6"
        @keyup.enter="verifyOtp"
        @input="onOtpInput"
        data-testid="otp-verification__code"
      />
      <v-btn
        class="otp-verification__submit mb-5 mt-4"
        color="primary"
        large
        block
        :disabled="verifying || !isValidOtp"
        :loading="verifying"
        @click="verifyOtp"
        data-testid="otp-verification__submit"
      >
        {{ $t('button.verify') }}
        <v-icon dark right>fa-check-circle</v-icon>
      </v-btn>
    </v-form>
    <v-btn text @click="goBackToSignIn" class="mb-5" data-testid="otp-verification__back">
      <v-icon left>fa-arrow-left</v-icon>
      {{ $t('button.back_to_signin') }}
    </v-btn>
  </div>
</template>

<i18n lang="yaml">
ja:
  label:
    otp_sent: 'ワンタイムパスワードをメールで送信しました'

  error:
    invalid_otp_code: 'ワンタイムパスワードが間違っています。再度お試しください'
    otp_expired: 'ワンタイムパスワードの有効期限が切れました。再度ログインしてください'
    no_otp_data: 'ワンタイムパスワードが見つかりません。再度ログインしてください'
    invalid_email: 'メールアドレスが無効です'
    verification_failed: '認証に失敗しました。再度お試しください'
    invalid_code_format: 'ワンタイムパスワードは6桁の数字で入力してください'
    otp_max_failures_exceeded: 'ワンタイムパスワードが5回失敗したため、ワンタイムパスワードが失効しました。再度ログインしてください'

  field:
    otp_code: 'ワンタイムパスワード (6桁)'

  button:
    verify: '認証する'
    back_to_signin: 'ログイン画面に戻る'

en:
  label:
    otp_sent: 'One-time password sent to your email'

  error:
    invalid_otp_code: 'Invalid one-time password. Please try again'
    otp_expired: 'One-time password has expired. Please sign in again'
    no_otp_data: 'No one-time password found. Please sign in again'
    invalid_email: 'Invalid email address'
    verification_failed: 'Verification failed. Please try again'
    invalid_code_format: 'Please enter a 6-digit one-time password'
    otp_max_failures_exceeded: 'One-time password has been disabled due to 5 failed attempts. Please sign in again'

  field:
    otp_code: 'One-Time Password (6 digits)'

  button:
    verify: 'Verify'
    back_to_signin: 'Back to Sign In'
</i18n>

<script>
export default {
  name: 'otp-verification',
  props: {
    email: {
      type: String,
      required: true,
    },
  },
  computed: {
    userEmail() {
      return this.email;
    },
    isValidOtp() {
      return this.model.otpCode && this.model.otpCode.length === 6 && /^\d{6}$/.test(this.model.otpCode);
    },
  },
  data() {
    return {
      verifying: false,
      error: '',
      model: {
        otpCode: '',
      },
      valid: false,
    };
  },
  methods: {
    onOtpInput() {
      // Clear error when user starts typing
      if (this.error) {
        this.error = '';
      }
      // Auto-format to numbers only
      this.model.otpCode = this.model.otpCode.replace(/\D/g, '');
    },
    verifyOtp: async function () {
      if (!this.isValidOtp) {
        this.error = this.$t('error.invalid_code_format');
        return;
      }

      this.verifying = true;
      try {
        const response = await this.$http.post('/api/verify_otp', {
          email: this.email,
          otp_code: this.model.otpCode,
        });

        await this.$store.dispatch('setToken', {
          token: response.data.access_token,
          keep: true,
        });
        // Success - redirect to main application
        this.$router.push(this.$route.query.redirectTo || '/');
      } catch (error) {
        this.onVerificationError(error);
      }
      this.verifying = false;
    },
    onVerificationError(error) {
      const errorCode = this._.get(error, 'response.data.error');
      const KNOWN_ERROR_CODES = [
        'invalid_otp_code', 'otp_expired', 'no_otp_data',
        'invalid_email', 'verification_failed', 'otp_max_failures_exceeded',
      ];

      if (KNOWN_ERROR_CODES.includes(errorCode)) {
        // Map backend error codes to frontend keys
        const errorMap = {
          invalid_otp_code: 'invalid_otp_code',
          otp_expired: 'otp_expired',
          no_otp_data: 'no_otp_data',
          invalid_email: 'invalid_email',
          verification_failed: 'verification_failed',
          otp_max_failures_exceeded: 'otp_max_failures_exceeded',
        };
        this.error = this.$t(`error.${errorMap[errorCode] || 'verification_failed'}`);

        // Auto-redirect to login for certain errors
        if (['otp_expired', 'no_otp_data', 'otp_max_failures_exceeded'].includes(errorCode)) {
          setTimeout(() => {
            this.goBackToSignIn();
          }, 3000); // Redirect after 3 seconds to allow user to read the error
        }
      } else {
        this.error = error.message || this.$t('error.verification_failed');
      }
    },
    goBackToSignIn() {
      this.$router.push('/sign_in');
    },
  },
};
</script>

<style scoped lang="sass">
.otp-verification
  ::v-deep .v-text-field__slot input
    padding: 1.5rem
    text-align: center
    font-size: 1.2rem
    letter-spacing: 0.2rem
</style>
